import { createApiClient, getApi } from './apiClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('MeRestaurantsService');

/**
 * One accessible-restaurant row returned by `GET /api/v1/users/me/locations`.
 *
 * The endpoint collapses the assignments → tenant-guid → restaurants-by-tenant
 * chain into a single call and returns every restaurant the signed-in user can
 * reach, each carrying the numeric ids the reports API needs.
 *
 * `employeeGuid` is the caller's own employee GUID at that restaurant (server-
 * resolved from their uid), or `null` when they have no linked employee record
 * there. It is additive + nullable — a row with a null `employeeGuid` is STILL
 * returned (no-lockout). The staff self-service surface uses it to decide
 * whether the signed-in account is a linked employee.
 *
 * `canAccessManagement` / `canAccessBooks` are per-restaurant app-access
 * capabilities computed server-side (Ecosystem Access Control). They are true
 * for the owner/admin role, else true when one of the caller's active job
 * assignments at that restaurant has a JobTitle carrying the matching access
 * flag. Clients use them to gate entry into the management / books apps.
 *
 * `jobTitles` is the caller's OWN active job-title label(s) at that restaurant,
 * present ONLY on staff-only rows (`canAccessManagement: false`) so a client can
 * tell the user what the account is assigned as on a "no management access"
 * screen. Additive + nullable (omitted/undefined on manageable rows and on older
 * servers). Labels only — no wage/tip data.
 */
export interface MeLocation {
  readonly tenantId: number;
  readonly tenantDocId: string;
  readonly tenantGuid?: string | null;
  readonly tenantName?: string | null;
  readonly restaurantId: number;
  readonly restaurantDocId: string;
  readonly restaurantGuid?: string | null;
  readonly restaurantName: string;
  readonly role: 'owner' | 'admin' | 'manager' | 'accountant' | 'staff';
  readonly employeeGuid: string | null;
  readonly canAccessManagement: boolean;
  readonly canAccessBooks: boolean;
  readonly jobTitles?: readonly string[] | null;
  /**
   * The location's default POS language (BCP-47, e.g. 'es'), or null when it
   * has none. Additive: absent from older servers.
   */
  readonly defaultLanguage?: string | null;
}

interface MeLocationsEnvelope {
  readonly success: boolean;
  readonly data: { readonly restaurants: MeLocation[] };
  readonly meta: {
    readonly timestamp: string;
    readonly correlationId: string;
    readonly version: string;
  };
}

const client = createApiClient('/api/v1/users');

/**
 * Per-request ceiling for this call, deliberately far below the shared client's
 * 120s backstop.
 *
 * This request gates the entire management app: `ManagementAccessGate` shows
 * "Still loading your workspace" once it has waited 15s
 * (`MANAGEMENT_READY_ESCALATION_MS`). With only the 120s default, a stalled
 * request kept the query in-flight for a further 105 SECONDS after that
 * message appeared — and then React Query retried it. The user was left
 * staring at an error whose retry could not resolve faster than the socket,
 * so the only effective action was a browser refresh.
 *
 * 20s is chosen to bound the failure without cutting off a slow-but-WORKING
 * response: the server resolves this per accessible restaurant (employee
 * lookup + capability resolution per row), so a large tenant can legitimately
 * be slow, and a tighter ceiling would manufacture failures where none existed.
 * Paired with the reduced retry count in `useMeRestaurants`, the worst case
 * drops from ~8 minutes (4 attempts × 120s) to ~41s — and the gate's timeout
 * notice self-heals the moment an attempt lands, because it renders only while
 * `escalated && !ready`.
 */
const ME_LOCATIONS_TIMEOUT_MS = 20_000;

export const meLocationsService = {
  /**
   * Fetch the flat list of restaurants the authenticated user can access.
   * Goes through the centralized {@link createApiClient} (Bearer token, App
   * Check, session-cookie + 401-retry) — never a parallel axios instance.
   */
  async getMyRestaurants(): Promise<MeLocation[]> {
    const startedAt = performance.now();
    logger.debug('Fetching accessible restaurants for current user');
    try {
      const body = await getApi<MeLocationsEnvelope>(client, '/me/locations', {
        timeout: ME_LOCATIONS_TIMEOUT_MS,
      });
      // Logged on every boot: this call gates first paint, so its real-world
      // latency needs to be observable rather than inferred.
      logger.info('Accessible restaurants loaded', {
        durationMs: Math.round(performance.now() - startedAt),
        rowCount: body.data.restaurants.length,
      });
      return body.data.restaurants;
    } catch (error) {
      logger.warn('Accessible restaurants failed', {
        durationMs: Math.round(performance.now() - startedAt),
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  },
};
